import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit'
import { SmartChart } from '../platinum-table-old/smart-chart'
import { PlatinumTableData } from '../platinum-table-old/types'
import { VisualizationSlide as SlideConfig } from '../../fixtures/visualizations/slide-config'

import brandAwarenessAgeTable from '../../fixtures/visualizations/tables/brand-awareness-age.json'
import brandAwarenessOverallTable from '../../fixtures/visualizations/tables/brand-awareness-overall.json'
import customerSatisfactionTrendTable from '../../fixtures/visualizations/tables/satisfaction-trend.json'
import productUsageFrequencyTable from '../../fixtures/visualizations/tables/usage-frequency.json'

const TABLE_DATA: Record<string, any> = {
  'brand-awareness-age.json': brandAwarenessAgeTable,
  'brand-awareness-overall.json': brandAwarenessOverallTable,
  'satisfaction-trend.json': customerSatisfactionTrendTable,
  'usage-frequency.json': productUsageFrequencyTable
}

interface VisualizationSlideProps {
  slide: SlideConfig
  isFullscreen?: boolean
  onToggleFullscreen?: () => void
}

export const VisualizationSlide: React.FC<VisualizationSlideProps> = ({
  slide,
  isFullscreen = false,
  onToggleFullscreen
}) => {
  const [tableData, setTableData] = useState<PlatinumTableData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadTableData()
  }, [slide.tableFile])

  const loadTableData = async () => {
    if(!slide.tableFile) return
    try {
      setLoading(true)
      setError(null)
      setTableData(TABLE_DATA[slide.tableFile])
    } catch (err) {
      console.error(`Failed to load table data from ${slide.tableFile}:`, err)
      
      // Try to create a fallback empty table structure
      setTableData(createFallbackData(slide))
      setError(`Note: Using sample data. Original file ${slide.tableFile} not found.`)
    } finally {
      setLoading(false)
    }
  }

  const createFallbackData = (slide: SlideConfig): PlatinumTableData => {
    // Create a simple fallback structure for missing files
    return {
      SchemaVersion: "1.0",
      Name: slide.tableName,
      CreatedUtc: new Date().toISOString(),
      Meta: {
        Top: "Sample Columns",
        Side: "Sample Rows",
        Filter: undefined,
        Weight: undefined,
        UnweightedBase: 1000,
        FilteredBase: 1000,
        SigLevel: 95
      },
      Top: {
        Vecs: [
          { Type: "Base", Label: "Column A" },
          { Type: "Base", Label: "Column B" }
        ]
      },
      Side: {
        Vecs: [
          { Type: "Base", Label: "Row 1" },
          { Type: "Base", Label: "Row 2" }
        ]
      },
      Cells: {
        Rows: [
          {
            Cell: [
              { Freq: 250, Colpc: 0.5, Rowpc: 0.5 },
              { Freq: 250, Colpc: 0.5, Rowpc: 0.5 }
            ]
          },
          {
            Cell: [
              { Freq: 250, Colpc: 0.5, Rowpc: 0.5 },
              { Freq: 250, Colpc: 0.5, Rowpc: 0.5 }
            ]
          }
        ]
      }
    }
  }

  if (loading) {
    return (
      <Paper 
        elevation={3} 
        sx={{ 
          p: 3, 
          height: isFullscreen ? '100vh' : '600px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <CircularProgress />
      </Paper>
    )
  }

  return (
    <Paper 
      elevation={3} 
      sx={{ 
        p: 3,
        height: isFullscreen ? '100vh' : 'auto',
        minHeight: isFullscreen ? '100vh' : '600px',
        display: 'flex',
        flexDirection: 'column',
        position: isFullscreen ? 'fixed' : 'relative',
        top: isFullscreen ? 0 : 'auto',
        left: isFullscreen ? 0 : 'auto',
        right: isFullscreen ? 0 : 'auto',
        bottom: isFullscreen ? 0 : 'auto',
        zIndex: isFullscreen ? 1300 : 'auto',
        bgcolor: 'background.paper'
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h5" component="h2" gutterBottom>
            {slide.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {slide.description}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Chip 
              label={slide.tableName} 
              size="small" 
              color="primary" 
              variant="outlined"
              icon={<InfoIcon />}
            />
            <Chip 
              label={`Chart: ${slide.chartType}`} 
              size="small" 
              variant="outlined"
            />
            <Chip 
              label={`Data: ${slide.dataType}`} 
              size="small" 
              variant="outlined"
            />
          </Box>
        </Box>
        <Box>
          {onToggleFullscreen && (
            <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}>
              <IconButton onClick={onToggleFullscreen}>
                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Chart Display */}
      <Box sx={{ flexGrow: 1, minHeight: 400, height: '100%' }}>
        {tableData && (
          <SmartChart
            platinumData={tableData}
            displayName={slide.tableName}
            chartKind={slide.chartType}
            selectedDataType={slide.dataType}
            chartSettings={slide.chartSettings}
            displayOptions={{
              ShowFreq: true,
              ShowColPct: true,
              ShowRowPct: true,
              ShowSig: false
            }}
          />
        )}
      </Box>
    </Paper>
  )
}